// src/components/DevTools/MetricsPanel.tsx

import React from 'react'
import {
  ChevronDown,
  ChevronUp,
  X,
  TrendingUp,
  Accessibility,
  Globe,
  Lock,
  Zap as ZapIcon,
  BarChart3,
  FileText,
  Info,
} from 'lucide-react'
import {
  EnhancedLighthouseMetrics,
  DetailedAccessibilityIssue,
  ComprehensiveSEOMetrics,
  SecurityMetrics,
} from '../../types'
import { getPerformanceClass, getVitalStatus } from '../../utils/helpers'

interface MetricsPanelProps {
  deviceName: string
  isExpanded: boolean
  activeTab: 'performance' | 'accessibility' | 'seo' | 'security'
  performanceData?: EnhancedLighthouseMetrics
  accessibilityData?: DetailedAccessibilityIssue[]
  seoData?: ComprehensiveSEOMetrics
  securityData?: SecurityMetrics
  onToggleExpand: () => void
  onClose: () => void
  onTabChange: (tab: 'performance' | 'accessibility' | 'seo' | 'security') => void
}

export function MetricsPanel({
  deviceName,
  isExpanded,
  activeTab,
  performanceData,
  accessibilityData,
  seoData,
  securityData,
  onToggleExpand,
  onClose,
  onTabChange,
}: MetricsPanelProps) {
  return (
    <div className="floating-metrics">
      <div className="metrics-header">
        <div className="metrics-title">Quality Analysis • {deviceName}</div>
        <div className="metrics-controls">
          <button
            onClick={onToggleExpand}
            className="action-btn"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <button onClick={onClose} className="action-btn" title="Close metrics">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="metrics-content">
          <div className="tab-navigation">
            <button
              onClick={() => onTabChange('performance')}
              className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
            >
              <TrendingUp className="w-3 h-3" style={{ marginRight: '4px' }} />
              Performance
            </button>
            <button
              onClick={() => onTabChange('accessibility')}
              className={`tab-btn ${activeTab === 'accessibility' ? 'active' : ''}`}
            >
              <Accessibility className="w-3 h-3" style={{ marginRight: '4px' }} />
              Accessibility
            </button>
            <button
              onClick={() => onTabChange('seo')}
              className={`tab-btn ${activeTab === 'seo' ? 'active' : ''}`}
            >
              <Globe className="w-3 h-3" style={{ marginRight: '4px' }} />
              SEO
            </button>
            <button
              onClick={() => onTabChange('security')}
              className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            >
              <Lock className="w-3 h-3" style={{ marginRight: '4px' }} />
              Security
            </button>
          </div>

          {activeTab === 'performance' && performanceData && (
            <>
              <div className="lighthouse-scores">
                {[
                  { key: 'performance', label: 'Performance', value: performanceData.performance },
                  {
                    key: 'accessibility',
                    label: 'Accessibility',
                    value: performanceData.accessibility,
                  },
                  {
                    key: 'bestPractices',
                    label: 'Best Practices',
                    value: performanceData.bestPractices,
                  },
                  { key: 'seo', label: 'SEO', value: performanceData.seo },
                  { key: 'pwa', label: 'PWA', value: performanceData.pwa },
                ].map(({ key, label, value }) => {
                  const perfClass = getPerformanceClass(value)
                  return (
                    <div key={key} className="score-item">
                      <div className="score-value" style={{ color: perfClass.color }}>
                        {value}
                      </div>
                      <div className="score-label">{label}</div>
                      <div
                        className="score-status"
                        style={{
                          background: perfClass.color + '20',
                          color: perfClass.color,
                        }}
                      >
                        {perfClass.label}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="vitals-section">
                <div className="section-title">
                  <ZapIcon className="w-4 h-4" />
                  Core Web Vitals
                </div>
                <div className="vitals-grid">
                  {[
                    {
                      key: 'loadTime',
                      label: 'Load Time',
                      value: performanceData.loadTime,
                      unit: 'ms',
                      benchmark: 'Good: < 2000ms',
                    },
                    {
                      key: 'firstContentfulPaint',
                      label: 'First Contentful Paint',
                      value: performanceData.firstContentfulPaint,
                      unit: 'ms',
                      benchmark: 'Good: < 1800ms',
                    },
                    {
                      key: 'largestContentfulPaint',
                      label: 'Largest Contentful Paint',
                      value: performanceData.largestContentfulPaint,
                      unit: 'ms',
                      benchmark: 'Good: < 2500ms',
                    },
                    {
                      key: 'cumulativeLayoutShift',
                      label: 'Cumulative Layout Shift',
                      value: performanceData.cumulativeLayoutShift,
                      unit: '',
                      benchmark: 'Good: < 0.1',
                    },
                    {
                      key: 'firstInputDelay',
                      label: 'First Input Delay',
                      value: performanceData.firstInputDelay,
                      unit: 'ms',
                      benchmark: 'Good: < 100ms',
                    },
                    {
                      key: 'totalBlockingTime',
                      label: 'Total Blocking Time',
                      value: performanceData.totalBlockingTime,
                      unit: 'ms',
                      benchmark: 'Good: < 200ms',
                    },
                  ].map(({ key, label, value, unit, benchmark }) => {
                    const status = getVitalStatus(key, value)
                    const StatusIcon = status.icon || Info

                    return (
                      <div key={key} className="vital-item">
                        <div className="vital-content">
                          <div className="vital-label">{label}</div>
                          <div className="vital-value" style={{ color: status.color }}>
                            {value}
                            {unit}
                          </div>
                          <div className="vital-benchmark">{benchmark}</div>
                        </div>
                        <div className="vital-status">
                          <StatusIcon
                            style={{ width: '20px', height: '20px', color: status.color }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="vitals-section">
                <div className="section-title">
                  <BarChart3 className="w-4 h-4" />
                  Additional Metrics
                </div>
                <div className="performance-grid">
                  <div className="performance-item">
                    <div className="performance-label">Total Size</div>
                    <div className="performance-value">{performanceData.totalSize} KB</div>
                  </div>
                  <div className="performance-item">
                    <div className="performance-label">Requests</div>
                    <div className="performance-value">{performanceData.requestCount}</div>
                  </div>
                  <div className="performance-item">
                    <div className="performance-label">Cache Hit Rate</div>
                    <div className="performance-value">{performanceData.cacheHitRatio}%</div>
                  </div>
                  <div className="performance-item">
                    <div className="performance-label">Image Optimization</div>
                    <div className="performance-value">{performanceData.imageOptimization}%</div>
                  </div>
                  <div className="performance-item">
                    <div className="performance-label">Text Compression</div>
                    <div className="performance-value">{performanceData.textCompression}%</div>
                  </div>
                  <div className="performance-item">
                    <div className="performance-label">Unused Code</div>
                    <div className="performance-value">{performanceData.unusedCode}%</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'accessibility' && accessibilityData && (
            <div className="accessibility-section">
              <div className="section-title">
                <Accessibility className="w-4 h-4" />
                Accessibility Issues Found
              </div>
              {accessibilityData.map((issue, index) => (
                <div key={index} className="accessibility-issue">
                  <span className={`issue-severity severity-${issue.severity}`}>
                    {issue.severity}
                  </span>
                  <div className="issue-content">
                    <div className="issue-message">{issue.message}</div>
                    <div className="issue-details">
                      <span>Element: {issue.element}</span>
                      {issue.count && <span>Count: {issue.count}</span>}
                      <span>Impact: {issue.impact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'seo' && seoData && (
            <div className="seo-section">
              <div className="section-title">
                <Globe className="w-4 h-4" />
                SEO Analysis • Score: {seoData.score}/100
              </div>
              <div className="seo-grid">
                <div className="seo-category">
                  <div className="seo-category-title">
                    <FileText className="w-4 h-4" />
                    Content
                  </div>
                  <div className="seo-checklist">
                    <div className="seo-item">
                      <span
                        className={
                          seoData.title.exists && seoData.title.optimal ? 'seo-check' : 'seo-fail'
                        }
                      >
                        {seoData.title.exists && seoData.title.optimal ? '✓' : '✗'}
                      </span>
                      <span>Title Tag ({seoData.title.length} chars)</span>
                    </div>
                    <div className="seo-item">
                      <span
                        className={
                          seoData.metaDescription.exists && seoData.metaDescription.optimal
                            ? 'seo-check'
                            : 'seo-fail'
                        }
                      >
                        {seoData.metaDescription.exists && seoData.metaDescription.optimal
                          ? '✓'
                          : '✗'}
                      </span>
                      <span>Meta Description ({seoData.metaDescription.length} chars)</span>
                    </div>
                    <div className="seo-item">
                      <span className={seoData.headings.optimal ? 'seo-check' : 'seo-fail'}>
                        {seoData.headings.optimal ? '✓' : '✗'}
                      </span>
                      <span>Heading Structure (H1: {seoData.headings.h1Count})</span>
                    </div>
                    <div className="seo-item">
                      <span
                        className={seoData.imageAlts.percentage > 80 ? 'seo-check' : 'seo-fail'}
                      >
                        {seoData.imageAlts.percentage > 80 ? '✓' : '✗'}
                      </span>
                      <span>Image Alt Texts ({seoData.imageAlts.percentage}%)</span>
                    </div>
                  </div>
                </div>
                <div className="seo-category">
                  <div className="seo-category-title">
                    <ZapIcon className="w-4 h-4" />
                    Technical
                  </div>
                  <div className="seo-checklist">
                    <div className="seo-item">
                      <span className={seoData.https ? 'seo-check' : 'seo-fail'}>
                        {seoData.https ? '✓' : '✗'}
                      </span>
                      <span>HTTPS</span>
                    </div>
                    <div className="seo-item">
                      <span className={seoData.mobileFriendly ? 'seo-check' : 'seo-fail'}>
                        {seoData.mobileFriendly ? '✓' : '✗'}
                      </span>
                      <span>Mobile Friendly</span>
                    </div>
                    <div className="seo-item">
                      <span className={seoData.canonicalUrl ? 'seo-check' : 'seo-fail'}>
                        {seoData.canonicalUrl ? '✓' : '✗'}
                      </span>
                      <span>Canonical URL</span>
                    </div>
                    <div className="seo-item">
                      <span className={seoData.structuredData ? 'seo-check' : 'seo-fail'}>
                        {seoData.structuredData ? '✓' : '✗'}
                      </span>
                      <span>Structured Data</span>
                    </div>
                  </div>
                </div>
              </div>
              {seoData.recommendations.length > 0 && (
                <div className="recommendations">
                  <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                    Recommendations
                  </div>
                  {seoData.recommendations.slice(0, 4).map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      {rec}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'security' && securityData && (
            <div className="security-section">
              <div className="section-title">
                <Lock className="w-4 h-4" />
                Security Analysis • Score: {securityData.score}/100
              </div>
              <div className="seo-checklist">
                <div className="seo-item">
                  <span className={securityData.https ? 'seo-check' : 'seo-fail'}>
                    {securityData.https ? '✓' : '✗'}
                  </span>
                  <span>HTTPS Encryption</span>
                </div>
                <div className="seo-item">
                  <span className={securityData.hsts ? 'seo-check' : 'seo-fail'}>
                    {securityData.hsts ? '✓' : '✗'}
                  </span>
                  <span>HSTS Header</span>
                </div>
                <div className="seo-item">
                  <span className={securityData.contentSecurityPolicy ? 'seo-check' : 'seo-fail'}>
                    {securityData.contentSecurityPolicy ? '✓' : '✗'}
                  </span>
                  <span>Content Security Policy</span>
                </div>
                <div className="seo-item">
                  <span className={securityData.xFrameOptions ? 'seo-check' : 'seo-fail'}>
                    {securityData.xFrameOptions ? '✓' : '✗'}
                  </span>
                  <span>X-Frame-Options</span>
                </div>
                <div className="seo-item">
                  <span className={!securityData.mixedContent ? 'seo-check' : 'seo-fail'}>
                    {!securityData.mixedContent ? '✓' : '✗'}
                  </span>
                  <span>No Mixed Content</span>
                </div>
                <div className="seo-item">
                  <span className={securityData.vulnerabilities === 0 ? 'seo-check' : 'seo-fail'}>
                    {securityData.vulnerabilities === 0 ? '✓' : '✗'}
                  </span>
                  <span>Vulnerabilities: {securityData.vulnerabilities}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
